import React, { useState, useCallback, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import { CaseDetail } from '../../../../models/CaseDetail'
import { colors } from '../../../../theme/palette'
import { Button } from '../../../ui/Button/Button'
import { Typography } from '../../../ui/Typography/Typography'
import { cardProps, control } from '../../../../theme/reuseables'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import { uploadFile } from '../../../../api/salary'
import { L } from '../../../providers/Lokalise'
import {
    Close,
    IkonFilliste,
    IkonPromptfejl,
    IkonTilføjfil,
} from '../../../../assets/icons'
import { Circle, CircleIcon } from '../../../ui/Circle/Circle'
import { ErrorDisplay } from '../Contact/ContactDialog'
import { SimpleDialog } from '../../../ui/Dialog/SimpleDialog'

type CaseFileCreateProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
    onCancel?: () => void
    isOpen?: boolean
}

const useStyles = createUseStyles({
    control: {
        ...control,
    },
    card: {
        ...cardProps.card,
    },
    title: {
        ...cardProps.title,
    },
    contentWrapper: {
        padding: '100px 20px 20px',
        overflow: 'auto',
        height: 'calc(100% - 100px)',
        paddingBottom: '80px',
        position: 'absolute',
        top: 0,
        right: 0,
        width: '100%',
        transform: 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        '&.visible': {
            transform: 'translateX(0)',
        },
    },
    bottom: {
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        width: '100%',
        padding: '20px',
        left: '0',
        background: colors.white,
        zIndex: 4,
        borderTop: `1px solid ${colors.borderColor}`,
        '& > button': {
            flexGrow: 1,
        },
        '& > button:last-of-type': {
            marginRight: '0',
        },
    },
    dropZone: {
        position: 'relative',
        border: 'none',
        borderRadius: '0.5rem',
        padding: '3rem 2rem',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        backgroundColor: colors.white,
        backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${colors.black.replace(
            '#',
            '%23'
        )}' stroke-width='2' stroke-dasharray='24%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        '&:hover': {
            backgroundColor: colors.gray,
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${colors.green.replace(
                '#',
                '%23'
            )}' stroke-width='2' stroke-dasharray='24%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        },
        '&.dragging': {
            backgroundColor: colors.gray,
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${colors.green.replace(
                '#',
                '%23'
            )}' stroke-width='2' stroke-dasharray='24%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
        },
    },
    fileInput: {
        display: 'none',
    },
    fileInfo: {
        marginTop: '1rem',
        color: colors.labelColor,
    },
    button: {
        backgroundColor: colors.green,
        color: colors.white,
        border: 'none',
        borderRadius: '0.5rem',
        padding: '.4rem 2rem',
        cursor: 'pointer',
        fontSize: '1.1rem',
        fontWeight: '500',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: colors.green600,
        },
        '&:focus': {
            outline: 'none',
            boxShadow: `0 0 0 2px ${colors.white}, 0 0 0 4px ${colors.green}`,
        },
    },
})

type RegistrationFormFields = {
    reminderAarsagFritekst: string
}

const CaseFileCreateView: React.FC<CaseFileCreateProps> = ({
    caseDetails,
    onUpdated,
    onCancel,
    isOpen = false,
}): JSX.Element => {
    const classes = useStyles()
    const [isLoading, setIsLoading] = useState(false)
    const [isDragging, setIsDragging] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')
    const { register, handleSubmit, formState, reset, setValue } =
        useForm<RegistrationFormFields>()

    const resetForm = (): void => {
        reset()
        setSelectedFile(null)
        setIsDragging(false)
        setErrorMessage('')
        setOpenErrorDialog(false)
        setIsLoading(false)
    }

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        setIsDragging(false)
    }, [])

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            setIsDragging(false)
            const file = e.dataTransfer.files[0]
            if (file) {
                setSelectedFile(file)
                setValue('reminderAarsagFritekst', file.name)
            }
        },
        [setValue]
    )

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            e.preventDefault()
            e.stopPropagation()
            const file = e.target.files?.[0]
            if (file) {
                setSelectedFile(file)
                setValue('reminderAarsagFritekst', file.name)
            }
        },
        [setValue]
    )

    const handleClick = useCallback(() => {
        const fileInput = document.getElementById(
            'fileInput'
        ) as HTMLInputElement
        if (fileInput) {
            fileInput.value = '' // Reset the input to allow selecting the same file again
            fileInput.click()
        }
    }, [])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            document.getElementById('fileInput')?.click()
        }
    }, [])

    const onSubmit = async (data: RegistrationFormFields): Promise<void> => {
        if (!selectedFile) {
            return
        }

        setIsLoading(true)

        try {
            const response = await uploadFile(selectedFile, caseDetails.caseId)
            if (response.error) {
                setIsLoading(false)
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
            } else {
                onUpdated?.()
            }
        } catch (error) {
            console.error('Error uploading file:', error)
            setIsLoading(false)
            setErrorMessage('Der skete en fejl - kontakt support')
            setOpenErrorDialog(true)
            resetForm()
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isOpen) {
            resetForm()
        }
    }, [isOpen])

    return (
        <>
            <div
                className={`${classes.contentWrapper} ${
                    isOpen ? 'visible' : 'visible'
                }`}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Typography
                        tag="h2"
                        size="XL"
                        align="center"
                        weight="bold"
                        style={{ margin: '20px 0 10px' }}
                    >
                        {L('LØN_TILFØJ_FIL_DIALOG_TITEL')}
                    </Typography>
                    <FormControl>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                            }}
                        >
                            <Typography tag="p" size="XS">
                                {L('LØN_TILFØJ_FIL_DIALOG_FILNAVN_LABEL')}
                            </Typography>
                            <div
                                className={`${classes.dropZone} ${
                                    isDragging ? 'dragging' : ''
                                }`}
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={handleClick}
                                    onKeyDown={handleKeyDown}
                                    aria-label="Upload file"
                                >
                                    <div>
                                        <IkonTilføjfil
                                            width="56px"
                                            fill={colors.green500}
                                        />
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginBottom: '1rem',
                                            maxWidth: '130px',
                                            textAlign: 'center',
                                            margin: '.5rem auto 1.5rem',
                                        }}
                                    >
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            align="center"
                                        >
                                            {L(
                                                'LØN_TILFØJ_FIL_DIALOG_DROPZONE_TEXT'
                                            )}
                                        </Typography>
                                    </div>

                                    <input
                                        id="fileInput"
                                        type="file"
                                        className={classes.fileInput}
                                        onChange={handleFileSelect}
                                        accept="*/*"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className={classes.button}
                                    onClick={handleClick}
                                >
                                    {L('LØN_TILFØJ_FIL_DIALOG_VÆLG_FIL_KNAP')}
                                </button>
                            </div>
                            {selectedFile && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '0.5rem',
                                        border: `1px solid ${colors.borderColor}`,
                                        borderRadius: '0.5rem',
                                        padding: '1rem 1rem',
                                        marginTop: '1rem',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '1.5rem',
                                            position: 'relative',
                                            top: '3px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <IkonFilliste fill={colors.green500} />
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                wordBreak: 'break-all',
                                            }}
                                        >
                                            <Typography
                                                tag="p"
                                                size="XXS"
                                                weight="bold"
                                                style={{
                                                    display: 'flex',
                                                    gap: '0.5rem',
                                                    justifyContent:
                                                        'space-between',
                                                    marginBottom: '0rem',
                                                    paddingBottom: '0rem',
                                                    lineHeight: '1',
                                                    height: '100%',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {selectedFile.name.lastIndexOf(
                                                    '.'
                                                ) !== -1
                                                    ? selectedFile.name.substring(
                                                          0,
                                                          selectedFile.name.lastIndexOf(
                                                              '.'
                                                          )
                                                      )
                                                    : selectedFile.name}
                                            </Typography>
                                        </div>
                                        <div style={{ flexShrink: 0 }}>
                                            <Typography
                                                tag="p"
                                                size="XXS"
                                                weight="bold"
                                                style={{
                                                    display: 'flex',
                                                    gap: '0.5rem',
                                                    justifyContent:
                                                        'space-between',
                                                    marginBottom: '0rem',
                                                    paddingBottom: '0rem',
                                                    lineHeight: '1',
                                                    height: '100%',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {selectedFile.name.lastIndexOf(
                                                    '.'
                                                ) !== -1
                                                    ? selectedFile.name.substring(
                                                          selectedFile.name.lastIndexOf(
                                                              '.'
                                                          )
                                                      )
                                                    : ''}
                                            </Typography>
                                        </div>
                                    </div>
                                    <Button
                                        type="action"
                                        size="small"
                                        onClick={() => setSelectedFile(null)}
                                        style={{
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Circle marginRight=".5rem">
                                            <CircleIcon>
                                                <Close width="16px" />
                                            </CircleIcon>
                                        </Circle>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </FormControl>
                </form>
            </div>
            <div className={classes.bottom}>
                <Button onClick={onCancel} type="secondary" size="large">
                    {L('LØN_TILFØJ_FIL_DIALOG_ANNULLER_KNAP')}
                </Button>
                <Button
                    buttonType="submit"
                    type="primary"
                    size="large"
                    loading={isLoading}
                    onClick={handleSubmit(onSubmit)}
                >
                    {L('LØN_TILFØJ_FIL_DIALOG_UPLOAD_KNAP')}
                </Button>
            </div>
            {openErrorDialog && (
                <SimpleDialog
                    title="Fejl"
                    message={errorMessage}
                    closeDialog={() => setOpenErrorDialog(false)}
                    isError
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                />
            )}
        </>
    )
}

export default CaseFileCreateView
